// src/components/onboarding/CSVImportStep.jsx
// CSV import alternative for portfolio value onboarding step.
// Parses holdings CSV and calculates total portfolio value.

import { useState, useRef } from 'react';

const GREEN   = '#3ddc84';
const SURFACE = '#0d1117';
const BORDER  = '#1c2333';
const ERROR   = '#ff6b35';
const MUTED   = '#8b949e';

const EXAMPLE_CSV = `ticker,quantity,purchase_price,purchase_date
AAPL,10,150.00,2024-01-15
BTC,0.5,42000.00,2024-03-01`;

function parseCSV(text) {
  const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');

  const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
  const tickerIdx   = headers.indexOf('ticker');
  const qtyIdx      = headers.indexOf('quantity');
  const priceIdx    = headers.indexOf('purchase_price');

  if (tickerIdx === -1 || qtyIdx === -1 || priceIdx === -1) {
    throw new Error('Missing required columns: ticker, quantity, purchase_price');
  }

  const holdings = [];
  for (let i = 1; i < lines.length; i++) {
    const cols   = lines[i].split(',').map(c => c.trim());
    const ticker = cols[tickerIdx];
    const qty    = parseFloat(cols[qtyIdx]);
    const price  = parseFloat(cols[priceIdx]);

    if (!ticker) throw new Error(`Row ${i + 1}: missing ticker`);
    if (isNaN(qty) || qty <= 0) throw new Error(`Row ${i + 1}: invalid quantity "${cols[qtyIdx]}"`);
    if (isNaN(price) || price <= 0) throw new Error(`Row ${i + 1}: invalid purchase_price "${cols[priceIdx]}"`);

    holdings.push({ ticker, quantity: qty, purchasePrice: price, value: qty * price });
  }

  return holdings;
}

export default function CSVImportStep({ onComplete, onManual }) {
  const [dragging, setDragging]   = useState(false);
  const [holdings, setHoldings]   = useState(null);
  const [parseError, setParseError] = useState(null);
  const [saving, setSaving]       = useState(false);
  const fileInputRef              = useRef(null);

  const totalValue = holdings?.reduce((sum, h) => sum + h.value, 0) ?? 0;

  function handleFile(file) {
    if (!file) return;
    setParseError(null);
    setHoldings(null);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = parseCSV(e.target.result);
        setHoldings(parsed);
      } catch (err) {
        setParseError(err.message);
      }
    };
    reader.readAsText(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  function handleFileInput(e) {
    handleFile(e.target.files?.[0]);
  }

  async function handleConfirm() {
    if (!holdings || totalValue <= 0 || saving) return;
    setSaving(true);
    try {
      await onComplete(totalValue, 'USD');
    } catch {
      setParseError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100%',
      padding: '40px 24px',
    }}>
      <style>{`
        @keyframes csv-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .csv-card { animation: csv-fade-up .4s ease forwards; }
      `}</style>

      <div className="csv-card" style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        width: 'min(520px, 100%)',
        padding: '40px 36px',
        boxShadow: '0 0 60px rgba(61,220,132,.06), 0 24px 48px rgba(0,0,0,.5)',
      }}>
        {/* Header */}
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 18,
          fontWeight: 700,
          color: '#c9d1d9',
          letterSpacing: '.02em',
          marginBottom: 8,
        }}>
          Import portfolio from CSV
        </div>
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: MUTED,
          marginBottom: 24,
          lineHeight: 1.6,
        }}>
          Upload a CSV with your holdings and we'll calculate your total portfolio value.
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? GREEN : 'rgba(61,220,132,.3)'}`,
            borderRadius: 8,
            padding: '32px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'rgba(61,220,132,.04)' : 'rgba(61,220,132,.01)',
            transition: 'border-color .15s, background .15s',
            marginBottom: 20,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <div style={{ fontSize: 28, marginBottom: 10, opacity: dragging ? 1 : 0.5 }}>⬆</div>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: dragging ? GREEN : MUTED,
            letterSpacing: '.06em',
            marginBottom: 6,
          }}>
            {dragging ? 'DROP FILE HERE' : 'DRAG & DROP CSV · OR CLICK TO BROWSE'}
          </div>
          <div style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'rgba(139,148,158,.5)',
          }}>
            .csv files only
          </div>
        </div>

        {/* Expected format */}
        {!holdings && !parseError && (
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              color: MUTED,
              letterSpacing: '.1em',
              marginBottom: 8,
            }}>
              EXPECTED FORMAT
            </div>
            <pre style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: '#8b949e',
              background: '#0a0a0a',
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              padding: '12px 14px',
              margin: 0,
              overflowX: 'auto',
              lineHeight: 1.6,
            }}>
              {EXAMPLE_CSV}
            </pre>
          </div>
        )}

        {/* Parse error */}
        {parseError && (
          <div style={{
            background: 'rgba(255,107,53,.08)',
            border: `1px solid rgba(255,107,53,.25)`,
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 20,
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: ERROR,
            letterSpacing: '.04em',
          }}>
            ⚠ {parseError}
          </div>
        )}

        {/* Holdings breakdown */}
        {holdings && (
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              color: MUTED,
              letterSpacing: '.1em',
              marginBottom: 8,
            }}>
              CALCULATED BREAKDOWN
            </div>
            <div style={{
              background: '#0a0a0a',
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              overflow: 'hidden',
              marginBottom: 12,
            }}>
              {holdings.map((h, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 14px',
                  borderBottom: i < holdings.length - 1 ? `1px solid ${BORDER}` : 'none',
                }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: GREEN, fontWeight: 700, width: 60 }}>
                    {h.ticker}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#8b949e' }}>
                    {h.quantity} × ${h.purchasePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#c9d1d9', fontWeight: 700 }}>
                    ${h.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
            {/* Total */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              background: 'rgba(61,220,132,.06)',
              border: `1px solid rgba(61,220,132,.2)`,
              borderRadius: 6,
            }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: MUTED, letterSpacing: '.08em' }}>
                TOTAL PORTFOLIO VALUE
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: GREEN }}>
                ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}

        {/* Confirm button */}
        {holdings && (
          <button
            onClick={handleConfirm}
            disabled={saving}
            style={{
              width: '100%',
              background: saving ? 'rgba(61,220,132,.5)' : GREEN,
              border: 'none',
              color: '#0a0a0a',
              padding: '14px 0',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'default' : 'pointer',
              letterSpacing: '.1em',
              transition: 'box-shadow .15s',
              boxShadow: saving ? 'none' : '0 0 20px rgba(61,220,132,.25)',
              marginBottom: 16,
            }}
          >
            {saving ? 'SAVING…' : 'CONFIRM THIS VALUE →'}
          </button>
        )}

        {/* Manual entry link */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onManual}
            style={{
              background: 'transparent',
              border: 'none',
              color: MUTED,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              cursor: 'pointer',
              letterSpacing: '.06em',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(139,148,158,.4)',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = GREEN; }}
            onMouseLeave={e => { e.currentTarget.style.color = MUTED; }}
          >
            Enter manually instead
          </button>
        </div>
      </div>
    </div>
  );
}
